import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type {
  PlayoffBracket as PlayoffBracketData,
  PlayoffCell,
  PlayoffEntrant,
  PlayoffMatchup,
  PlayoffRegionBracket,
  PlayoffSeriesGame,
  PlayoffSide,
} from '../types'
import { TeamHelmet } from './TeamHelmet'
import { playerProfilePath } from './ui'
import './PlayoffBracket.css'

/**
 * Draws a season's postseason.
 *
 * @remarks
 * The one renderer for both the public playoffs page and the admin editor's
 * live preview, so what an admin builds against is the thing the league sees
 * rather than an approximation of it. Everything it needs is already resolved
 * server-side — who is in each slot, who won, what each round is called — and
 * this file adds only layout.
 *
 * Within a region the connector lines are pure CSS: rounds are equal-height
 * flex columns, so a cell always sits at the midpoint of the two that feed it
 * and a half-gap elbow reaches it without anything being measured. The one
 * line that cannot work that way is the cross-conference rail into the
 * championship, which spans two independently sized brackets; that one is
 * measured. See {@link useFinalRail}.
 *
 * @example
 * ```tsx
 * <PlayoffBracket bracket={bracket} />
 * <PlayoffBracket bracket={bracket} selectedMatchupId={editing} onSelectMatchup={setEditing} />
 * ```
 */
export function PlayoffBracket({
  bracket,
  onSelectMatchup,
  selectedMatchupId,
  linkPlayers = true,
}: {
  bracket: PlayoffBracketData
  /** Makes every series clickable; the admin editor opens its result form. */
  onSelectMatchup?: (matchup: PlayoffMatchup) => void
  selectedMatchupId?: number | null
  /** Off inside the editor, where a click should open the series, not leave. */
  linkPlayers?: boolean
}) {
  const { containerRef, rail, cellHeight } = useBracketMetrics(bracket)

  return (
    <div
      className="pob-wrap"
      style={cellHeight === null ? undefined : ({ '--pob-cell-h': `${cellHeight}px` } as React.CSSProperties)}
    >
      <BracketHeader bracket={bracket} />

      <div className="pob-scroll">
        <div
          className={`pob${bracket.championship ? ' pob--has-final' : ''}`}
          ref={containerRef}
        >
          <div className="pob__regions">
            {bracket.regions.map(region => (
              <RegionBracket
                key={region.region}
                region={region}
                feedsFinal={bracket.championship !== null}
                onSelectMatchup={onSelectMatchup}
                selectedMatchupId={selectedMatchupId}
                linkPlayers={linkPlayers}
              />
            ))}
          </div>

          {/* Only the series box is centred on the rail; its label and the
              champion tag hang off it so neither shifts that centre. */}
          {bracket.championship && (
            <div
              className="pob__final"
              style={rail ? { marginTop: rail.offset } : undefined}
            >
              <div className="pob-round__label pob__final-label">
                {bracket.championship.label}
              </div>
              {rail && (
                <span
                  className="pob__rail"
                  aria-hidden="true"
                  style={{ top: `calc(50% + ${rail.top - rail.center}px)`, height: rail.height }}
                />
              )}
              <span className="pob-cell__in" aria-hidden="true" />
              <MatchupBox
                matchup={bracket.championship}
                onSelect={onSelectMatchup}
                selected={selectedMatchupId === bracket.championship.id}
                linkPlayers={linkPlayers}
              />
              {bracket.champion && <ChampionTag entrant={bracket.champion} />}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface FinalRail {
  /** Distance from the top of the bracket to the midpoint of the two finals. */
  center: number
  /** Top of the vertical rail, in the same coordinates. */
  top: number
  height: number
  /**
   * `margin-top` that puts the series box on `center`.
   *
   * @remarks
   * Half the box's own height, not a CSS `translateY(-50%)`. A transform on
   * this wrapper would make the hover panel's `position: fixed` resolve against
   * the championship instead of the viewport, which is why the title series
   * used to open a panel that vanished or appeared off-screen.
   */
  offset: number
}

/** Clearance between a series box and the one below it. */
const CELL_PADDING = 20

/**
 * The two things about this layout that CSS cannot work out for itself.
 *
 * @returns `rail` positions the championship series, and is null until both
 * conference finals are on screen or for a bracket with no championship.
 * `cellHeight` is the row pitch every round shares, and is null before the
 * first measurement.
 *
 * @remarks
 * Everything else here is implied by the flex layout; these two are measured,
 * reluctantly, because neither is expressible in CSS.
 *
 * The rail spans two conference brackets sized independently — six AFC players
 * against three NFC ones give their finals different heights — so the point
 * halfway between them is not a percentage of anything.
 *
 * The row pitch has to be uniform or the connectors stop meeting: every elbow
 * assumes a cell sits exactly at the midpoint of the two feeding it, which only
 * holds while all cells share a height. A series carrying game lines is taller
 * than a bare one, so that shared height is whatever the tallest series in the
 * bracket needs, and it can only be known once drawn. The floor comes from
 * `--pob-cell-min`, which is what keeps the narrow-viewport rules in force.
 *
 * Watching a `ResizeObserver` rather than measuring once keeps both right
 * through a window resize and through a bracket growing as an admin fills it
 * in. Neither measurement depends on the value it produces — a series box is
 * sized by its own content — so this settles after one pass rather than
 * chasing itself.
 */
function useBracketMetrics(bracket: PlayoffBracketData) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [rail, setRail] = useState<FinalRail | null>(null)
  const [cellHeight, setCellHeight] = useState<number | null>(null)

  const measure = useCallback(() => {
    const node = containerRef.current
    if (!node) return

    const boxes = node.querySelectorAll('.pob-matchup, .pob-bye')
    const tallest = Math.max(0, ...Array.from(boxes, box => box.getBoundingClientRect().height))
    const floor = Number.parseFloat(
      getComputedStyle(node).getPropertyValue('--pob-cell-min')
    )
    setCellHeight(
      Math.round(Math.max(Number.isFinite(floor) ? floor : 0, tallest + CELL_PADDING))
    )

    if (!bracket.championship) {
      setRail(null)
      return
    }

    const anchors = node.querySelectorAll('[data-final-anchor]')
    if (anchors.length < 2) {
      setRail(null)
      return
    }

    const base = node.getBoundingClientRect().top
    const centers = Array.from(anchors, anchor => {
      const box = anchor.getBoundingClientRect()
      return box.top + box.height / 2 - base
    })

    const top = Math.min(...centers)
    const bottom = Math.max(...centers)
    const center = (top + bottom) / 2
    const finalBox = node.querySelector('.pob__final')
    const half = (finalBox instanceof HTMLElement ? finalBox.offsetHeight : 0) / 2
    setRail({ center, top, height: bottom - top, offset: Math.round(center - half) })
  }, [bracket])

  useLayoutEffect(() => {
    const node = containerRef.current
    if (!node) return

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [measure])

  return { containerRef, rail, cellHeight }
}

/** Whether a series has anything the hover panel would show. */
function matchupHasDetails(matchup: PlayoffMatchup): boolean {
  return matchup.games.length > 0 || Boolean(matchup.notes)
}

/** Whether any series has games or a note, which is what the hover hint is about. */
function hasHoverContent(bracket: PlayoffBracketData): boolean {
  const inRegions = bracket.regions.some(region =>
    region.rounds.some(round =>
      round.cells.some(cell => cell.kind === 'matchup' && matchupHasDetails(cell.matchup))
    )
  )
  return inRegions || (bracket.championship !== null && matchupHasDetails(bracket.championship))
}

function BracketHeader({ bracket }: { bracket: PlayoffBracketData }) {
  return (
    <div className="pob-header">
      <div className="pob-header__meta">
        <span className="pob-chip">
          {bracket.format === 'conference' ? 'AFC / NFC' : 'Single bracket'}
        </span>
        <span className="pob-chip">
          {bracket.seriesBestOf === 1 ? 'Single game' : `Best of ${bracket.seriesBestOf}`}
        </span>
        <span className="pob-chip">
          {bracket.pairing === 'reseed' ? 'Reseeds each round' : 'Fixed draw'}
        </span>
        {!bracket.complete && <span className="pob-chip pob-chip--live">In progress</span>}
        {hasHoverContent(bracket) && (
          <span className="pob-header__hint">
            Hover a series for its games and notes, then click a game for the box score.
          </span>
        )}
      </div>

      {bracket.champion && (
        <div className="pob-header__champion">
          <span className="pob-header__trophy" aria-hidden="true">
            🏆
          </span>
          <TeamHelmet abbreviation={bracket.champion.teamAbbreviation} size="md" />
          <span className="pob-header__name">{bracket.champion.displayName}</span>
          {bracket.runnerUp && (
            <span className="pob-header__over">
              over {bracket.runnerUp.displayName}
              {bracket.championship?.seriesLabel ? `, ${bracket.championship.seriesLabel}` : ''}
            </span>
          )}
        </div>
      )}

      {bracket.notes && <p className="pob-header__notes">{bracket.notes}</p>}
    </div>
  )
}

function RegionBracket({
  region,
  feedsFinal,
  onSelectMatchup,
  selectedMatchupId,
  linkPlayers,
}: {
  region: PlayoffRegionBracket
  /** True when this region's last round runs on into a championship series. */
  feedsFinal: boolean
  onSelectMatchup?: (matchup: PlayoffMatchup) => void
  selectedMatchupId?: number | null
  linkPlayers: boolean
}) {
  if (region.rounds.length === 0) {
    return (
      <section className="pob-region pob-region--empty">
        <h3 className="pob-region__label">{region.label}</h3>
        <p className="muted">
          {region.entrants.length === 1
            ? `${region.entrants[0].displayName} advanced without playing a round.`
            : 'No players seeded yet.'}
        </p>
      </section>
    )
  }

  // Every round is as tall as the first, so a cell lands exactly between the
  // two that feed it and the elbows need no arithmetic.
  const rows = region.rounds[0]?.cells.length ?? 1

  return (
    <section className="pob-region" style={{ '--pob-rows': rows } as React.CSSProperties}>
      <h3 className="pob-region__label">{region.label}</h3>

      <div className="pob-region__rounds">
        {/* A short conference is pushed right so both finals share a column. */}
        {Array.from({ length: region.columnOffset }).map((_, index) => (
          <div key={`spacer-${index}`} className="pob-round pob-round--spacer" />
        ))}

        {region.rounds.map(round => {
          const isLast = round.round === region.rounds.length
          return (
            <div className="pob-round" key={round.round}>
              <div className="pob-round__label">{round.label}</div>
              <div className="pob-round__cells">
                {round.cells.map(cell => (
                  <div className="pob-cell" key={cellKey(cell)}>
                    <div
                      className="pob-cell__body"
                      data-final-anchor={isLast && feedsFinal ? '' : undefined}
                    >
                      {cell.kind === 'bye' ? (
                        <ByeBox entrant={cell.entrant} linkPlayers={linkPlayers} />
                      ) : (
                        <MatchupBox
                          matchup={cell.matchup}
                          onSelect={onSelectMatchup}
                          selected={selectedMatchupId === cell.matchup.id}
                          linkPlayers={linkPlayers}
                        />
                      )}
                    </div>
                    {(!isLast || feedsFinal) && (
                      <span
                        className={`pob-cell__out${isLast ? ' pob-cell__out--straight' : ''}`}
                        aria-hidden="true"
                      />
                    )}
                    {round.round > 1 && <span className="pob-cell__in" aria-hidden="true" />}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function cellKey(cell: PlayoffCell): string {
  return cell.kind === 'bye' ? `bye-${cell.slot}` : `m-${cell.matchup.id}`
}

function MatchupBox({
  matchup,
  onSelect,
  selected,
  linkPlayers,
}: {
  matchup: PlayoffMatchup
  onSelect?: (matchup: PlayoffMatchup) => void
  selected?: boolean
  linkPlayers: boolean
}) {
  const className = [
    'pob-matchup',
    `pob-matchup--${matchup.status}`,
    selected ? 'pob-matchup--selected' : '',
    onSelect ? 'pob-matchup--clickable' : '',
  ]
    .filter(Boolean)
    .join(' ')

  // A 2-0 series with a blank beside the loser reads as a missing number, so
  // both sides show their count as soon as either has one.
  const showWins = matchup.top.wins > 0 || matchup.bottom.wins > 0

  const body = (
    <Fragment>
      <SideRow side={matchup.top} showWins={showWins} linkPlayers={linkPlayers && !onSelect} />
      <SideRow side={matchup.bottom} showWins={showWins} linkPlayers={linkPlayers && !onSelect} />
      {matchup.games.length > 0 && (
        <span className="pob-matchup__count">
          {matchup.games.length} game{matchup.games.length === 1 ? '' : 's'}
        </span>
      )}
      {matchup.notes && <span className="pob-matchup__note">{matchup.notes}</span>}
    </Fragment>
  )

  const box = onSelect ? (
    <button
      type="button"
      className={className}
      onClick={() => onSelect(matchup)}
      aria-label={`Edit ${matchup.label}`}
    >
      {body}
    </button>
  ) : (
    <div className={className}>{body}</div>
  )

  if (!matchupHasDetails(matchup)) {
    return box
  }

  return <SeriesDetails matchup={matchup}>{box}</SeriesDetails>
}

/**
 * Reveals a series' games and note on hover, focus or tap.
 *
 * @remarks
 * The games used to sit inside the series box, which does not survive contact
 * with a real bracket: three lines of helmets under every box turns a filled
 * best-of-three field into a wall. Moving them into a panel keeps the drawn
 * bracket readable and buys room to say more per game than a pair of helmets —
 * who was P1, and a way through to the box score.
 *
 * Positioned `fixed` against the anchor's measured rect rather than absolutely
 * inside it, because the bracket scrolls horizontally and an absolutely placed
 * panel would be clipped by that scroll container at exactly the edges where
 * it is most needed. That only works while no ancestor has a CSS `transform`
 * — see {@link FinalRail.offset} for why the championship is centred with
 * margin rather than `translateY(-50%)`.
 *
 * Hover alone would strand touch users, so the panel also opens on tap and on
 * keyboard focus, and closes on Escape or on any scroll that would leave it
 * pointing at nothing.
 */
function SeriesDetails({
  matchup,
  children,
}: {
  matchup: PlayoffMatchup
  children: React.ReactNode
}) {
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const [placement, setPlacement] = useState<{ top: number; left: number; above: boolean } | null>(
    null
  )

  const open = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect()
    if (!rect) return

    // Flip above the box when the viewport bottom is closer than the panel is
    // tall, so a series in the last round is not pinned off-screen.
    const estimatedHeight =
      56 + matchup.games.length * 30 + (matchup.notes ? 48 : 0)
    const above = rect.bottom + estimatedHeight > window.innerHeight && rect.top > estimatedHeight

    setPlacement({
      top: above ? rect.top - 6 : rect.bottom + 6,
      left: rect.left + rect.width / 2,
      above,
    })
  }, [matchup.games.length, matchup.notes])

  const close = useCallback(() => setPlacement(null), [])

  useEffect(() => {
    if (!placement) return

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('scroll', close, true)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', close, true)
    }
  }, [placement, close])

  return (
    <div
      className="pob-anchor"
      ref={anchorRef}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
      onClick={() => (placement ? close() : open())}
    >
      {children}
      {placement && (
        <div
          className={`pob-pop${placement.above ? ' pob-pop--above' : ''}`}
          style={{ top: placement.top, left: placement.left }}
          role="dialog"
          aria-label={
            matchup.games.length > 0 ? `${matchup.label} games` : `${matchup.label} note`
          }
        >
          <p className="pob-pop__title">{matchup.label}</p>
          {matchup.notes && <p className="pob-pop__notes">{matchup.notes}</p>}
          {matchup.games.length > 0 && (
            <ul className="pob-pop__list">
              {matchup.games.map(game => (
                <SeriesGameLine key={game.gameId} game={game} matchup={matchup} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * One game of a series, P1 first, linking through to its box score.
 *
 * @remarks
 * Ordered by who held the controller rather than by bracket position, matching
 * how a result is read aloud and how the Games list prints it. That means the
 * higher seed is not always on the left, which is the point: the line says who
 * was P1 without needing a label on every row.
 *
 * The winning score is the emphasised one, so who took a game reads without
 * comparing two numbers.
 */
function SeriesGameLine({
  game,
  matchup,
}: {
  game: PlayoffSeriesGame
  matchup: PlayoffMatchup
}) {
  const p1IsTop = game.p1 === 'top'

  const p1 = {
    name: (p1IsTop ? matchup.top : matchup.bottom).entrant?.displayName ?? 'TBD',
    team: p1IsTop ? game.topTeamAbbreviation : game.bottomTeamAbbreviation,
    score: p1IsTop ? game.topScore : game.bottomScore,
  }
  const p2 = {
    name: (p1IsTop ? matchup.bottom : matchup.top).entrant?.displayName ?? 'TBD',
    team: p1IsTop ? game.bottomTeamAbbreviation : game.topTeamAbbreviation,
    score: p1IsTop ? game.bottomScore : game.topScore,
  }
  const p1Won = p1.score > p2.score

  return (
    <li>
      <Link className="pob-pop__game" to={`/games/${game.gameId}`}>
        <span className="pob-pop__num">G{game.gameNumber}</span>

        <span className="pob-pop__side" title={`${p1.name} (P1)`}>
          <TeamHelmet abbreviation={p1.team} size="sm" />
          <span className={`pob-pop__score${p1Won ? ' pob-pop__score--won' : ''}`}>
            {p1.score}
          </span>
        </span>

        <span className="pob-pop__dash">–</span>

        <span className="pob-pop__side" title={p2.name}>
          <span className={`pob-pop__score${p1Won ? '' : ' pob-pop__score--won'}`}>
            {p2.score}
          </span>
          <TeamHelmet abbreviation={p2.team} size="sm" />
        </span>

        <span className="pob-pop__p1">P1 {p1.name}</span>
      </Link>
    </li>
  )
}

function SideRow({
  side,
  showWins,
  linkPlayers,
}: {
  side: PlayoffSide
  showWins: boolean
  linkPlayers: boolean
}) {
  if (!side.entrant) {
    return (
      <div className="pob-side pob-side--pending">
        <span className="pob-side__seed" aria-hidden="true" />
        <span className="pob-side__name">{side.placeholder ?? 'TBD'}</span>
      </div>
    )
  }

  return (
    <div className={`pob-side${side.isWinner ? ' pob-side--winner' : ''}`}>
      <span className="pob-side__seed">{side.entrant.seed}</span>
      <TeamHelmet abbreviation={side.entrant.teamAbbreviation} size="sm" />
      {linkPlayers ? (
        <Link to={playerProfilePath(side.entrant.leaguePlayerId)} className="pob-side__name">
          {side.entrant.displayName}
        </Link>
      ) : (
        <span className="pob-side__name">{side.entrant.displayName}</span>
      )}
      <span className="pob-side__wins">{showWins ? side.wins : ''}</span>
    </div>
  )
}

function ByeBox({
  entrant,
  linkPlayers,
}: {
  entrant: PlayoffEntrant | null
  linkPlayers: boolean
}) {
  if (!entrant) {
    return <div className="pob-bye pob-bye--empty">Bye</div>
  }

  return (
    <div className="pob-bye">
      <span className="pob-side__seed">{entrant.seed}</span>
      <TeamHelmet abbreviation={entrant.teamAbbreviation} size="sm" />
      {linkPlayers ? (
        <Link to={playerProfilePath(entrant.leaguePlayerId)} className="pob-side__name">
          {entrant.displayName}
        </Link>
      ) : (
        <span className="pob-side__name">{entrant.displayName}</span>
      )}
      <span className="pob-bye__tag">Bye</span>
    </div>
  )
}

function ChampionTag({ entrant }: { entrant: PlayoffEntrant }) {
  return (
    <div className="pob-champion">
      <span aria-hidden="true">🏆</span>
      <TeamHelmet abbreviation={entrant.teamAbbreviation} size="sm" />
      <span className="pob-champion__name">{entrant.displayName}</span>
    </div>
  )
}
